/**
 * Copy dos três blocos do fio agêntico que faltavam superfície: a pesquisa na
 * internet, o código executado e o arquivo gerado no turno
 * (spec-001 `pesquisa-internet-chat`, [T101] e [T106]).
 *
 * Arquivo próprio porque `constants/TextChat.js` está em 506 linhas — acima do
 * teto de 500 do repo. Nenhum literal de UI nasce dentro de componente.
 *
 * ★ As três frases da [T106]/[US-8] são `PAGINA_NAO_LIDA`, `TETO_ATINGIDO` e
 * `SEM_RESULTADO`. Toda mensagem de falha aqui diz também O QUE FAZER — uma
 * frase que só descreve o erro deixa o consultor no mesmo lugar.
 */

export const BUSCA_WEB = Object.freeze({
  TITULO: 'Pesquisa na internet',
  // Nome acessível do botão que abre/fecha a lista das buscas.
  ARIA_LISTA: 'Buscas na internet deste turno',
  // Enquanto a busca ainda não voltou.
  EM_ANDAMENTO: 'Pesquisando',
  // O rótulo da busca cuja `consulta` se perdeu (resultado sem par).
  CONSULTA_DESCONHECIDA: 'Busca sem consulta registrada',
  // Abre a fonte numa aba nova.
  ABRIR_FONTE: 'Abrir a fonte em nova aba',
  COLETADO_EM: 'coletado em',
  // Acima de três fontes a lista colapsa (edge case da US-6).
  VER_TODAS: (ocultas) =>
    `Ver mais ${ocultas} ${ocultas === 1 ? 'fonte' : 'fontes'}`,
  VER_MENOS: 'Ver menos',

  // ---- [T106] As três frases da US-8 -------------------------------------
  // IF a leitura de uma página falha, THEN dizer QUAL página não pôde ser lida.
  PAGINA_NAO_LIDA: (endereco) =>
    `Não consegui ler ${endereco}. O conteúdo dessa página não entrou na resposta. Abra o endereço para conferir.`,
  // IF o teto de buscas é atingido, THEN informar o limite e oferecer seguir.
  TETO_ATINGIDO:
    'O limite de buscas deste turno acabou. Faça uma nova pergunta para continuar pesquisando.',
  // IF a busca volta sem resultado, THEN dizer que não encontrou.
  SEM_RESULTADO: (consulta) =>
    `A busca por "${consulta}" não encontrou resultados. O que vem a seguir não veio da internet. Tente outros termos.`,

  // ---- Recusa pela guarda, antes de a consulta sair ----------------------
  RECUSADA_IDENTIFICADOR:
    'Esta busca não saiu da plataforma: a consulta levava dado de cliente. Refaça a pergunta sem nome, CPF, número de conta ou valor de patrimônio.',
  RECUSADA_DOMINIO: (ofensor) =>
    `Esta busca não saiu da plataforma: ${ofensor} não está na lista de domínios liberados para o seu escritório. Peça a liberação ao gestor.`,
  RECUSADA: (motivo) =>
    `Esta busca não saiu da plataforma (${motivo}). Tente outros termos ou contate o suporte.`,
  DOMINIOS_LIBERADOS: 'Domínios liberados:',
})

export const CODIGO_DO_TURNO = Object.freeze({
  TITULO: 'Código executado',
  ARIA_LISTA: 'Código que a IA executou neste turno',
  // De onde o código rodou (`d_codigo.sandbox`): o consultor precisa saber que
  // não foi na infraestrutura da AAWZ quando não foi.
  SANDBOX: Object.freeze({
    servico: 'no serviço da AAWZ',
    backend: 'no backend da AAWZ',
    provedor: 'no ambiente do provedor do modelo',
  }),
  SAIDA: 'Saída',
  VER_MAIS: 'Ver o trecho inteiro',
  VER_MENOS: 'Ver menos',
  RECUSADO: (motivo) =>
    `Este código não chegou a rodar (${motivo}). Reformule o pedido ou contate o suporte.`,
})

export const ARQUIVO_DO_TURNO = Object.freeze({
  TITULO: 'Arquivos gerados',
  ARIA_LISTA: 'Arquivos que a IA gerou neste turno',
  // `d_arquivo.tipo` — enum do contrato.
  TIPO: Object.freeze({
    anexo: 'anexo',
    artefato: 'artefato',
    manifesto: 'manifesto',
  }),
  VERSAO: (versao) => `versão ${versao}`,
  // [spec-003 T301] O que a análise produziu — o eixo do `content_type`, não o
  // `tipo` do contrato logo acima. É o que separa as US-4/5/6 na tela: sem
  // isto, planilha, documento e gráfico saem com o mesmo rótulo e o mesmo
  // ícone, e o consultor não sabe o que está prestes a baixar.
  FAMILIA: Object.freeze({
    planilha: 'planilha',
    documento: 'documento',
    grafico: 'gráfico',
    arquivo: 'arquivo',
  }),
  // O evento traz METADADO, nunca bytes: quem baixa é o bloco de entregas da
  // bolha persistida (`chat-message.vue::entregasDoTurno`, spec-008 T028).
  SEM_DOWNLOAD_AQUI: 'O download fica em Entregas, no fim da resposta.',
})

/**
 * [spec-003 T301 / US-5] O gráfico da análise, inline na conversa.
 *
 * "WHEN a análise produz gráfico THE system SHALL exibi-lo inline no chat como
 * PNG e disponibilizá-lo também como artefato baixável" — e, o que decide a
 * copy de falha: "IF a renderização falha THEN THE system SHALL entregar a
 * tabela e dizer que o gráfico não saiu."
 */
export const GRAFICO_DO_TURNO = Object.freeze({
  TITULO: 'Gráfico da análise',
  // Nome acessível da imagem quando o artefato não trouxe nome próprio.
  ALT_PADRAO: 'Gráfico gerado pela análise',
  BAIXAR: (nome) => `Baixar ${nome}`,
  CARREGANDO: 'Carregando o gráfico…',
  // ⚠️ A frase NOMEIA o que sobrou: a tabela e o arquivo continuam lá. Dizer só
  // "não foi possível carregar" deixaria o consultor achando que perdeu a
  // análise inteira por causa da imagem.
  NAO_SAIU: 'O gráfico não pôde ser exibido aqui. Os números estão na resposta e o arquivo continua em Entregas.',
})
